import React from 'react';
import { X } from 'lucide-react';
import type { FestsetzungHistorieEntry } from './FestsetzungsHistorieModal';

interface FestsetzungsHistorieModalHJ2Props {
    isOpen: boolean;
    onClose: () => void;
    currencyCode: string;
    employeeName: string;
    hj2Entries: FestsetzungHistorieEntry[];
}

const formatCurrency = (value: number | undefined, currencyCode: string, decimalPlaces = 0): string => {
    const num = Number(value) || 0;
    return `${new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(num)} ${currencyCode}`;
};

const formatFactor = (value: number | undefined): string => {
    const num = Number(value) || 0;
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const renderMetricCell = (label: string, value: React.ReactNode, valueClass = 'text-white') => (
    <div>
        <div className="text-slate-500 text-xs uppercase mb-1">{label}</div>
        <div className={`font-mono font-bold text-sm ${valueClass}`}>{value}</div>
    </div>
);

const renderEntry = (entry: FestsetzungHistorieEntry, currencyCode: string) => {
    const snap = entry.snapshot || {};

    return (
        <div
            key={entry.monat}
            className="border border-slate-700 rounded-lg p-4 bg-slate-800/50"
        >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 border-b border-slate-700 pb-2">
                <span className="text-white font-bold">{entry.monat}</span>
                {(entry.lockedAt || entry.lockedBy) && (
                    <div className="text-xs text-slate-500">
                        {entry.lockedAt && <span>{entry.lockedAt}</span>}
                        <span>{entry.lockedAt ? ' · ' : ''}{entry.lockedBy || 'Admin'}</span>
                    </div>
                )}
            </div>
            {entry.snapshot ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    {renderMetricCell('Arbeitstage', `${snap.arbeitstage ?? 0} Tage`)}
                    {renderMetricCell('Wochenstunden', `${snap.wochenstunden ?? 0} Std`)}
                    {renderMetricCell('Lohnfaktor', formatFactor(snap.lohnfaktor), 'text-amber-400')}
                    {renderMetricCell('Soll-Umsatz / Monat', formatCurrency(snap.sollUmsatzMonat, currencyCode), 'text-amber-400')}
                    {renderMetricCell('Effektiv Brutto', formatCurrency(snap.effektiverBruttolohn, currencyCode, 2), 'text-[#E6C299]')}
                    {renderMetricCell('Grundstundenlohn', formatCurrency(snap.stdLohnGrund, currencyCode, 2))}
                    {renderMetricCell('Effektivstundenlohn', formatCurrency(snap.stdLohnEffektiv, currencyCode, 2), 'text-emerald-400')}
                    {renderMetricCell('Stundenlohn gesamt', formatCurrency(snap.stdLohnGesamt, currencyCode, 2), 'text-white')}
                </div>
            ) : (
                <p className="text-slate-500 text-sm italic">Kein Monats-Snapshot vorhanden (Legacy-Daten).</p>
            )}
        </div>
    );
};

const FestsetzungsHistorieModalHJ2: React.FC<FestsetzungsHistorieModalHJ2Props> = ({
    isOpen,
    onClose,
    currencyCode,
    employeeName,
    hj2Entries,
}) => {
    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-white tracking-wide">Festsetzungs-Historie</h2>
                        <p className="text-sm text-slate-400 mt-0.5">{employeeName}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg p-2 transition-colors"
                        aria-label="Schließen"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto px-6 py-5 space-y-3">
                    {hj2Entries.map((entry) => renderEntry(entry, currencyCode))}
                </div>
            </div>
        </div>
    );
};

export default FestsetzungsHistorieModalHJ2;
